import type { V1AuthStartAccountActionData } from '@repo/sdk/gen'
import { z } from 'zod'
import type { TKey } from '@/lib/i18n'

type AccountAction = V1AuthStartAccountActionData['body']['action']

/**
 * What the success toast says, per action. Total by construction: an action
 * the contract adds without a sentence here is a compile error rather than a
 * toast with nothing in it.
 */
const SUCCESS_MESSAGE_KEYS = {
	'update-password': 'profile:result.success.update_password',
	'configure-otp': 'profile:result.success.configure_otp',
	'regenerate-recovery-codes':
		'profile:result.success.regenerate_recovery_codes',
	'remove-otp-device': 'profile:result.success.remove_otp_device'
} as const satisfies Record<AccountAction, TKey>

const ACCOUNT_ACTIONS = [
	'update-password',
	'configure-otp',
	'regenerate-recovery-codes',
	'remove-otp-device'
] as const satisfies readonly AccountAction[]

/**
 * The two parameters the callback appends on its way back from Keycloak.
 * Anything else in their place — a hand-edited URL, an old bookmark — reads as
 * absent rather than failing the page.
 */
export const accountActionResultSearchSchema = z.object({
	action: z.enum(ACCOUNT_ACTIONS).optional().catch(undefined),
	status: z
		.enum(['success', 'cancelled', 'error'])
		.optional()
		.catch(undefined)
})

export function accountActionResultKey(
	action: AccountAction,
	status: 'success' | 'cancelled' | 'error'
): TKey {
	if (status === 'success') {
		return SUCCESS_MESSAGE_KEYS[action]
	}
	return status === 'cancelled'
		? 'profile:result.cancelled'
		: 'profile:result.error'
}
